import { inject, Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
} from "@angular/router";
import { AuthService } from "../services/auth.service";
import { Pass } from "../../administration/models/pass.model";
import { SnackService } from "../../../shared/services/snack-service.service";
import { LocalStorageService } from "../../../shared/services/local-storage.service";
import { MatDialog } from "@angular/material/dialog";
import { DialogsService } from "../../../shared/services/dialogs/dialogs.service";
import { firstValueFrom } from "rxjs";
import { PassFacadeService } from "../../administration/services/pass-store/pass-facade.service";

@Injectable({
  providedIn: "root",
})
export class RouteGuard implements CanActivate {
  dialogService = inject(DialogsService);
  passFacade = inject(PassFacadeService);

  constructor(
    public authService: AuthService,
    private snackService: SnackService,
    private localStorageService: LocalStorageService,
    private dialog: MatDialog,
    private router: Router,
  ) {}

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<boolean> {
    if (
      this.authService.isSoftLogout() &&
      route.url?.[0]?.path !== "dashboard"
    ) {
      return this.handleSoftLogout();
    }
    const dialogs = this.dialog.openDialogs;
    dialogs.forEach((d) => d.close());
    const pass = this.passFacade.currentPass();

    if (!pass) {
      if (this.authService.userSignal()) {
        return this.handleAccess(
          this.localStorageService.getWithTTL("PASS"),
          state.url,
        );
      } else {
        this.router.navigateByUrl("/auth/login");
        return false;
      }
    } else {
      return this.handleAccess(pass, state.url);
    }
  }

  handleAccess(pass: Pass, url: string) {
    let canRoute = true;

    if (!pass) {
      this.localStorageService.putWithTTL("lastUrl", this.router.url, 1000);
      this.router.navigateByUrl("/auth/login");
      return false;
    }

    if (!pass.tiers && url.includes("tiers")) {
      canRoute = false;
    }

    if (
      !pass.specifs &&
      (url.includes("articles") || url.includes("specification"))
    ) {
      canRoute = false;
    }

    if (!pass.infos_journalieres && url.includes("info-journalieres")) {
      canRoute = false;
    }

    if (!pass.lignes && url.includes("lignes")) {
      canRoute = false;
    }

    if (!pass.taches && url.includes("lists")) {
      canRoute = false;
    }

    if (!pass.stocks && (url.includes("stock") || url.includes("lots"))) {
      canRoute = false;
    }

    if (
      !pass.plannif_prod &&
      (url.includes("of-plannif") || url.includes("article-planification"))
    ) {
      canRoute = false;
    }

    if (!pass.stats_prod && url.includes("stats")) {
      canRoute = false;
    }

    if (
      !pass.commandes &&
      (url.includes("avoir-fournisseur") ||
        url.includes("commandes") ||
        url.includes("facture-fournisseur"))
    ) {
      canRoute = false;
    }

    if (
      !pass.commandesClient &&
      (url.includes("avoir") ||
        url.includes("commandeclient") ||
        url.includes("facture") ||
        url.includes("expeditions"))
    ) {
      canRoute = false;
    }

    if (!canRoute) {
      this.snackService.info(
        "Vous n'avez pas les droits pour accéder à cette page",
      );
      return false;
    } else {
      return true;
    }
  }

  private async handleSoftLogout() {
    const dialogRef = this.authService.askLogin();
    await firstValueFrom(dialogRef.afterClosed());
    return true;
  }
}
